import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import { downloadAsXLS, specificBranchHasPermission } from "src/utils";
import FilterDrawer from "./filters";
import {
  useAddReorderRequestMutation,
  useDownloadReorders,
  useGetReordersList,
} from "src/api/inventory/reorder";
import { useGetBranches } from "src/api/generic";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import SecondaryChip from "src/shared/components/secondary-chip";
import { InventoryPermissions } from "src/constants/permissions";

const AllTable = () => {
  const { t } = useTranslation("purchasing");
  const { t: inventoryT } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const [open, setOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [type, setType] = useState("");
  const [selectedBranch, setSelectedBranch] = useState<number>();
  const [allOrders, setAllOrders] = useState<any[]>();
  const IsAllSelected =
    allOrders &&
    allOrders?.length > 0 &&
    !!allOrders?.every((item) => item.selected);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);

  const HeadText = [
    t("name"),
    t("SKU"),
    inventoryT("minLevel"),
    inventoryT("maxLevel"),
    inventoryT("currentLevel"),
    inventoryT("reorder"),
    generalT("status"),
  ];

  const filters = {
    branch_id: selectedBranch,
    ...(!!name && { name }),
    ...(!!sku && { sku }),
    ...(!!type && { status: type }),
  };

  // APIS
  const { data, refetch, isFetching } = useGetReordersList({
    page: pageNumber + 1,
    ...filters,
  });
  const {
    data: downloadedData,
    refetch: refetchDownload,
    isFetching: isFetchingDownload,
    status: downloadStatus,
  } = useDownloadReorders(filters);
  const { data: branchesData } = useGetBranches();
  const { mutate, status, isPending, error } = useAddReorderRequestMutation();

  // permissions
  const sendPermission = specificBranchHasPermission(
    InventoryPermissions.sendReorder,
    selectedBranch ?? 0,
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleDownload = () => {
    refetchDownload();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  const emptyDataArr = data?.data.length === 0;

  const handleChangeOrder = (id: number) => {
    setAllOrders((prevOrders) =>
      prevOrders?.map((order: any) =>
        order.id === id ? { ...order, selected: !order.selected } : order,
      ),
    );
  };

  const handleSelectAllChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAllOrders((prevOrders) =>
      prevOrders?.map((order) => ({
        ...order,
        selected: e.target.checked,
      })),
    );
  };

  const handleReorderRequest = () => {
    mutate({
      branch_id: Number(selectedBranch),
      items: (allOrders ?? [])
        ?.filter((item) => item.selected)
        .map((item) => item.id),
    });
  };

  useEffect(() => {
    if (!!branchesData) {
      setSelectedBranch(branchesData.data[0].id);
    }
  }, [branchesData]);

  useEffect(() => {
    if (data) {
      setTotal(data.meta?.total as number);
      setAllOrders(
        data.data.map((item: any) => {
          return { ...item, selected: false };
        }),
      );
    }
  }, [data]);

  useEffect(() => {
    if (downloadStatus === "success" && !!downloadedData) {
      const utf8CSVData = `\uFEFF${downloadedData}`;
      downloadAsXLS(utf8CSVData, "reorders");
    }
  }, [isFetchingDownload]);

  useEffect(() => {
    if (status === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Stack spacing={3} maxWidth={{ xs: "100%", md: `calc(100vw - 316px)` }}>
      <Box display={"flex"} justifyContent={"flex-end"}>
        <Stack
          direction={"row"}
          gap={2}
          flexWrap={"wrap"}
          mt={{ xs: 2, md: 5, lg: 0 }}
        >
          <Button
            color="tertiary"
            variant="outlined"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
            onClick={handleOpenFilter}
          >
            {t("filter")}
          </Button>
          <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="demo-simple-select-label">
                {t("branches")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label={t("branches")}
                size="small"
                value={`${selectedBranch}`}
                onChange={(e) => setSelectedBranch(+e.target.value)}
              >
                {/* <MenuItem value={10}>All</MenuItem> */}
                {branchesData?.data.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <LoadingButton
            sx={{
              border: "1px solid #98A2B3",
              height: "40px",
              fontWeight: 500,
              borderRadius: "4px",
              padding: "7px 10px",
              background: "#FCFCFD",
              fontSize: "16px",
            }}
            color="tertiary"
            variant="outlined"
            endIcon={<img src={DownloadIcon} alt="" />}
            loading={isFetchingDownload}
            onClick={handleDownload}
          >
            {t("download")}
          </LoadingButton>
          {sendPermission && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpen}
                disabled={isFetching}
              >
                {inventoryT("sendSelected")}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell
                align={"center"}
                sx={{
                  bgcolor: "#F9FAFB",
                }}
              >
                <Stack direction={"row"} alignItems={"center"}>
                  <Checkbox
                    checked={!!IsAllSelected}
                    onChange={handleSelectAllChange}
                  />
                  <Typography>{inventoryT("selectAll")}</Typography>
                </Stack>
              </TableCell>
              {HeadText.map((item) => (
                <TableCell
                  key={item}
                  align={"center"}
                  sx={{
                    bgcolor: "#F9FAFB",
                  }}
                >
                  <Typography
                    fontSize={"12px"}
                    color={"#475467"}
                    lineHeight={"18px"}
                    fontWeight={500}
                  >
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isFetching || !allOrders ? (
            <TableLoadingSkeleton rowsLength={15} cellsLength={8} />
          ) : (
            <TableBody>
              {allOrders.map((row: any, index) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align={"center"}>
                    <Checkbox
                      checked={row.selected}
                      onChange={(e) => {
                        handleChangeOrder(row.id);
                      }}
                      // disabled={row.status === "ordered"}
                    />
                  </TableCell>
                  <TableCell align="center">{row.name}</TableCell>
                  <TableCell align="center">{row.sku}</TableCell>
                  <TableCell align="center">{row.min_level ?? "--"}</TableCell>
                  <TableCell align="center">{row.max_level ?? "--"}</TableCell>
                  <TableCell align="center">{row.quantity}</TableCell>
                  <TableCell align="center">{row.reorder_quantity}</TableCell>
                  <TableCell align="center">
                    <SecondaryChip
                      label={row.status}
                      variant={
                        row.status === "noraml"
                          ? "success"
                          : row.status === "re-order"
                            ? "warning"
                            : "info"
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {emptyDataArr && <NoData />}
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />

      <FilterDrawer
        open={openFilter}
        name={name}
        setName={setName}
        sku={sku}
        setSku={setSku}
        type={type}
        setType={setType}
        handleClose={handleCloseFilter}
      />

      <ConfirmPopup
        open={open}
        item={""}
        handleClose={handleClose}
        handleConfirm={handleReorderRequest}
        noIcon
        isPending={isPending}
        title={inventoryT("sendSelected")}
        subtitle={generalT("areYouSureToConfirm")}
        confirmBtnText={generalT("confirm")}
      />
    </Stack>
  );
};
export default AllTable;
